export const GATEWAY_MAP_SIZE = 4_096;

// Published by Vulnona for Gateway_v0.21.7 (Gateway v0.21.738).
const VULNONA_GATEWAY_MIN_WORLD_Y = -607_000;
const VULNONA_GATEWAY_MAX_WORLD_Y = 509_000;
const VULNONA_GATEWAY_MIN_WORLD_X = -505_000;
const VULNONA_GATEWAY_MAX_WORLD_X = 607_000;

export function gatewayHudCoordinates(latitude: number, longitude: number): { lat: number; long: number } {
  return { lat: -latitude, long: longitude };
}

/** Converts the game's displayed HUD coordinates into the calibrated Renatus image plane. */
export function gatewayHudToMap(latitude: number, longitude: number): { lat: number; lng: number } {
  const pixelX = 0.0000681884085 * latitude + 0.003233908554 * longitude + 2047.086418;
  const pixelY = -0.003239586628 * latitude - 0.0000698914468 * longitude + 2039.830138;
  return { lat: GATEWAY_MAP_SIZE - pixelY, lng: pixelX };
}

/** Converts raw EVRIMA world Y/X into the calibrated Renatus image plane. */
export function gatewayWorldToMap(worldY: number, worldX: number): { lat: number; lng: number } {
  const hud = gatewayHudCoordinates(worldY, worldX);
  return gatewayHudToMap(hud.lat, hud.long);
}

/** Projects raw EVRIMA world Y/X onto Vulnona's current Gateway image plane. */
export function gatewayWorldToVulnonaMap(worldY: number, worldX: number): { lat: number; lng: number } {
  const pixelX = ((worldX - VULNONA_GATEWAY_MIN_WORLD_X)
    / (VULNONA_GATEWAY_MAX_WORLD_X - VULNONA_GATEWAY_MIN_WORLD_X)) * GATEWAY_MAP_SIZE;
  const pixelY = ((worldY - VULNONA_GATEWAY_MIN_WORLD_Y)
    / (VULNONA_GATEWAY_MAX_WORLD_Y - VULNONA_GATEWAY_MIN_WORLD_Y)) * GATEWAY_MAP_SIZE;
  return { lat: GATEWAY_MAP_SIZE - pixelY, lng: pixelX };
}

/** Converts Vulnona image-plane pixels back into raw EVRIMA world Y/X. */
export function gatewayVulnonaMapToWorld(mapX: number, mapY: number): { worldY: number; worldX: number } {
  const worldX = VULNONA_GATEWAY_MIN_WORLD_X
    + ((mapX / GATEWAY_MAP_SIZE) * (VULNONA_GATEWAY_MAX_WORLD_X - VULNONA_GATEWAY_MIN_WORLD_X));
  const worldY = VULNONA_GATEWAY_MIN_WORLD_Y
    + ((mapY / GATEWAY_MAP_SIZE) * (VULNONA_GATEWAY_MAX_WORLD_Y - VULNONA_GATEWAY_MIN_WORLD_Y));
  return { worldY, worldX };
}

/** Converts calibrated Renatus image-plane pixels back into raw EVRIMA world Y/X. */
export function gatewayMapToWorld(mapX: number, mapY: number): { worldY: number; worldX: number } {
  const pixelX = mapX;
  const pixelY = mapY;
  const a = 0.0000681884085;
  const b = 0.003233908554;
  const d = -0.003239586628;
  const e = -0.0000698914468;
  const adjustedX = pixelX - 2047.086418;
  const adjustedY = pixelY - 2039.830138;
  const determinant = (a * e) - (b * d);
  const hudLatitude = ((adjustedX * e) - (b * adjustedY)) / determinant;
  const longitude = ((a * adjustedY) - (adjustedX * d)) / determinant;
  return { worldY: -hudLatitude, worldX: longitude };
}

/**
 * Projects a true world-space radius onto the calibrated Gateway image.
 * The calibration contains slight rotation and shear, so a world circle is
 * not represented accurately by multiplying the radius by one pixel factor.
 */
export function gatewayWorldRadiusBoundary(
  centerMapX: number,
  centerMapY: number,
  radiusWorldUnits: number,
  segments = 64
): Array<{ mapX: number; mapY: number }> {
  const center = gatewayMapToWorld(centerMapX, centerMapY);
  const count = Math.max(12, Math.min(128, Math.round(segments)));
  return Array.from({ length: count }, (_, index) => {
    const angle = (index / count) * Math.PI * 2;
    const point = gatewayWorldToMap(
      center.worldY + (Math.sin(angle) * radiusWorldUnits),
      center.worldX + (Math.cos(angle) * radiusWorldUnits)
    );
    return { mapX: point.lng, mapY: GATEWAY_MAP_SIZE - point.lat };
  });
}

/** Projects a stored, Renatus-calibrated zone boundary onto Vulnona's current Gateway image. */
export function gatewayWorldRadiusBoundaryVulnona(
  centerMapX: number,
  centerMapY: number,
  radiusWorldUnits: number,
  segments = 64
): Array<{ mapX: number; mapY: number }> {
  const center = gatewayMapToWorld(centerMapX, centerMapY);
  const count = Math.max(12, Math.min(128, Math.round(segments)));
  return Array.from({ length: count }, (_, index) => {
    const angle = (index / count) * Math.PI * 2;
    const point = gatewayWorldToVulnonaMap(
      center.worldY + (Math.sin(angle) * radiusWorldUnits),
      center.worldX + (Math.cos(angle) * radiusWorldUnits)
    );
    return { mapX: point.lng, mapY: GATEWAY_MAP_SIZE - point.lat };
  });
}

/** Reprojects a stored Renatus image-plane point onto Vulnona's current Gateway image. */
export function gatewayStoredMapPointToVulnona(mapX: number, mapY: number): { mapX: number; mapY: number } {
  const world = gatewayMapToWorld(mapX, mapY);
  const point = gatewayWorldToVulnonaMap(world.worldY, world.worldX);
  return { mapX: point.lng, mapY: GATEWAY_MAP_SIZE - point.lat };
}

/** Converts a point selected on Vulnona's image into the stored Renatus-calibrated plane. */
export function gatewayVulnonaMapPointToStored(mapX: number, mapY: number): { mapX: number; mapY: number } {
  const world = gatewayVulnonaMapToWorld(mapX, mapY);
  const point = gatewayWorldToMap(world.worldY, world.worldX);
  return { mapX: point.lng, mapY: GATEWAY_MAP_SIZE - point.lat };
}
